'use client';

import { ShareButton } from '@ps/features/sharing/ShareButton';
import type { ShareTarget } from '@ps/features/sharing/shareTarget';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { useImageAverageColor } from '@ps/functions/hooks/useImageAverageColor';
import { Share2 } from 'lucide-react';

import { CourseBanner } from '../../shared/components/CourseBanner';
import { CourseDescription } from '../../shared/components/CourseDescription';
import { CourseHeroBackButton } from './CourseHeroBackButton';

const SHARE_CLASS =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-raise/90 text-sink shadow-md transition hover:bg-white active:scale-95';

interface Props {
  name: string;
  description?: string;
  /** Cover URL the caller resolved (mobile-first); tints the hero and feeds the banner. */
  bannerUrl?: string | null;
  /** Semantic parent the STANDALONE page hard-navigates Back to (a course → the bio home). */
  backHref: string;
  /** Rendered inside the intercept overlay → Back pops the overlay (`router.back`). */
  intercepted?: boolean;
  /** The page's bio context — makes the share link custom-domain aware. */
  bioContext: BioContext;
  /** What the hero shares (the course); omit to hide the share control. */
  shareTarget?: ShareTarget | null;
}

/**
 * The course detail hero — the interactive client island above the server-rendered
 * course body (chapters / videos). Tints the masked banner with its dominant
 * color (1:1 with the My Life card), overlays the back button, and renders the
 * course title + collapsible description. Lifted from the legacy `CourseDetailView`
 * hero so the visitor page keeps the same look while the body goes server-first.
 */
export function CourseDetailHero({
  name,
  description,
  bannerUrl,
  backHref,
  intercepted,
  bioContext,
  shareTarget,
}: Props) {
  const { color, contrastingText } = useImageAverageColor(bannerUrl);
  const heroStyle = color
    ? { backgroundColor: color, color: contrastingText ?? '#ffffff' }
    : undefined;

  const backButton = (
    <CourseHeroBackButton backHref={backHref} intercepted={intercepted} />
  );
  const shareButton = shareTarget ? (
    <ShareButton
      bioContext={bioContext}
      target={shareTarget}
      title={name}
      aria-label={`Share ${name}`}
      className={SHARE_CLASS}
    >
      <Share2 className="h-5 w-5" aria-hidden />
    </ShareButton>
  ) : null;

  return (
    <section className="relative w-full shrink-0" style={heroStyle}>
      {bannerUrl ? (
        <>
          <CourseBanner
            src={bannerUrl}
            alt={name}
            className="h-[180px] sm:h-[240px]"
            priority
          />
          <div className="from-scrim/50 pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b to-transparent" />
          <div className="absolute inset-x-0 top-0 flex items-center gap-2 px-4 pt-5">
            {backButton}
            {shareButton ? <div className="ml-auto">{shareButton}</div> : null}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 px-4 pt-6">
          {backButton}
          {shareButton ? <div className="ml-auto">{shareButton}</div> : null}
        </div>
      )}
      <div className="mx-auto w-full max-w-[936px] px-4 pb-5 pt-2">
        <CourseDescription description={description} title={name} />
      </div>
    </section>
  );
}
